import clsx from "clsx";
import { Ban, Mail, MessageSquarePlus, PackageCheck, Pencil, Printer, Undo2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { CommentView, WholesaleEmailView, WholesaleOrderDetail } from "../../../shared/contracts.js";
import { isPartsRole, type PartException } from "../../../shared/domain.js";
import {
  cancelWholesaleOrder,
  fetchWholesaleActivityPage,
  wholesaleCommentEndpoints,
  wholesaleQueryKeys,
  wholesaleReceiptEndpoints,
  type WholesaleMutationResultDto,
} from "../../api/wholesaleQueries.js";
import { useSession } from "../../app/AppProviders.js";
import { Button, buttonAttributes } from "../../ui/Button.js";
import { STATUS_PRESENTATION, StatusBadge } from "../../ui/StatusBadge.js";
import { ActivityHistory, formatCalendarDate, PartLinesTable } from "../cases/detail/CaseDetailContent.js";
import { CommentComposer } from "../cases/detail/CommentComposer.js";
import { formatInstant } from "../cases/detail/ActivityTimeline.js";
import type { ActivityContext } from "../cases/detail/activity-sentences.js";
import { CancelCaseDialog } from "../cases/parts-actions/CancelCaseDialog.js";
import { EmailFailureBanner } from "../cases/parts-actions/EmailFailureBanner.js";
import { ReverseVerificationDialog, reversibleLines } from "../cases/parts-actions/ReverseVerificationDialog.js";
import { exceptionLabel, remainingQuantity, VerifyArrivalDialog } from "../cases/parts-actions/VerifyArrivalDialog.js";
import { EmailCustomerDialog } from "./EmailCustomerDialog.js";
import { WholesaleOrderEditSheet } from "./WholesaleOrderEditSheet.js";
import { wholesaleTicketPath } from "./ticket/WholesaleTicketPage.js";

/**
 * The right-hand inspector of the wholesale workspace (spec §4.4): the WS
 * number, the customer with contact, phone and email, the reference and
 * R&R number, the status, the arrived time and the master ETA; the parts
 * with verified counts and ETAs; the actions — Verify arrival, Reverse
 * verification, Edit order, Email customer, Add comment, Cancel order;
 * every customer email sent from the order with its outcome; the comments;
 * and the sentence timeline.
 *
 * Like the case inspector it renders what the server returned and nothing
 * it derived: status, progress, the 36-hour window and every capability
 * arrive in `WholesaleOrderDetail`. Parts-only controls render only when
 * the server's capability *and* the session role both say Parts. The
 * arrival, reversal, cancellation and comment dialogs are the case ones,
 * handed this order's endpoints (one engine, two owners); Email customer
 * is this side's own and is **disabled with the hint** "No email on file
 * for <customer>" when the customer record has no address (spec §5.2).
 * Every successful action invalidates the cache — the parent's queries
 * refetch and hand a fresh `detail` back down — and is announced through
 * a polite live region.
 */

export interface WholesaleOrderInspectorProps {
  detail: WholesaleOrderDetail;
  className?: string;
}

/** The names the sentences may use: the detail's lines and the current customer by id. */
export function wholesaleActivityContext(detail: WholesaleOrderDetail): ActivityContext {
  return {
    side: "wholesale",
    lines: detail.partLines,
    people: { [detail.order.customer.id]: detail.order.customer.name },
  };
}

/** Why Email customer is unavailable, in words beside the button (spec §5.2). */
export function emailCustomerHint(
  reason: WholesaleOrderDetail["capabilities"]["emailCustomerDisabledReason"],
  customerName: string,
): string | null {
  switch (reason) {
    case "no_email":
      return `No email on file for ${customerName}`;
    case "not_arrived":
      return "Nothing has arrived yet.";
    case "cancelled":
      return "The order is cancelled.";
    default:
      return null;
  }
}

const EMAIL_STATE_LABELS: Readonly<Record<WholesaleEmailView["state"], string>> = {
  pending: "Waiting to send",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
};

export function WholesaleOrderInspector({ detail, className }: WholesaleOrderInspectorProps) {
  const { user } = useSession();
  const { order, partLines, comments, activity, emails, capabilities } = detail;
  const { customer } = order;
  const titleId = useId();
  const numberId = useId();
  const noteTitleId = useId();
  const linesTitleId = useId();
  const emailsTitleId = useId();
  const commentsTitleId = useId();
  const activityTitleId = useId();
  const emailHintId = useId();

  const isPartsSession = user !== null && isPartsRole(user.role);
  const cancelled = order.status === "cancelled";
  const canVerify = isPartsSession && capabilities.canVerifyArrival && !cancelled;
  const hasRemaining = partLines.some((line) => remainingQuantity(line) > 0);
  const hasReversible = reversibleLines(partLines).length > 0;
  const showVerify = canVerify && hasRemaining;
  const showReverse = canVerify && hasReversible;
  const showEdit = isPartsSession && capabilities.canEditOrder && !cancelled;
  // Any wholesale order may be printed, cancelled and historical ones
  // included — the same rule the service receipt follows, and the reason a
  // counter can reprint a ticket for an order that has already gone out.
  const showPrint = isPartsSession;
  const showCancel = isPartsSession && capabilities.canCancel && !cancelled;
  // The button stays visible but disabled with its hint while the order is
  // live and cannot be emailed (spec §5.2); a cancelled order has no button.
  const showEmail = isPartsSession && !cancelled;
  const emailHint = emailCustomerHint(capabilities.emailCustomerDisabledReason, customer.name);
  const emailEnabled = capabilities.canEmailCustomer && emailHint === null;
  const showComment = capabilities.canComment;

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // Switching to another order drops what belonged to the previous one.
  useEffect(() => {
    setAnnouncement("");
    setVerifyOpen(false);
    setReverseOpen(false);
    setEditOpen(false);
    setCancelOpen(false);
    setEmailOpen(false);
    setComposerOpen(false);
  }, [order.id]);

  const statusLabel = (result: WholesaleMutationResultDto) => STATUS_PRESENTATION[result.status].label;
  const onVerified = (result: WholesaleMutationResultDto) =>
    setAnnouncement(`Receipt saved. ${result.number} is now ${statusLabel(result)}.`);
  const onReversed = (result: WholesaleMutationResultDto) =>
    setAnnouncement(`Verification reversed. ${result.number} is now ${statusLabel(result)}.`);
  const onSaved = (result: WholesaleMutationResultDto) =>
    setAnnouncement(`Order saved. ${result.number} is ${statusLabel(result)}.`);
  const onExceptionChanged = (result: WholesaleMutationResultDto, partNumber: string, exception: PartException | null) =>
    setAnnouncement(
      exception
        ? `${exceptionLabel(exception)} flagged on ${partNumber}. ${result.number} is ${statusLabel(result)}.`
        : `Exception cleared on ${partNumber}. ${result.number} is ${statusLabel(result)}.`,
    );
  const onCancelled = (result: WholesaleMutationResultDto) =>
    setAnnouncement(`Order cancelled. ${result.number} is now ${statusLabel(result)}.`);
  const onCommentAdded = (_comment: CommentView) => setAnnouncement("Comment added.");
  const onEmailSent = (email: WholesaleEmailView) => setAnnouncement(`Customer email queued for ${email.to}.`);

  // Every customer email is watched: the banner stays silent while one is
  // pending or sent and speaks — with Retry — once a delivery has failed,
  // polling the event itself while it is in flight (the worker usually
  // fails after the detail was read). It is handed the state the detail
  // printed in the Customer emails list, and re-reads the order whenever
  // its poll disagrees, so the list never says "Waiting to send" under a
  // banner that says the send failed.

  return (
    <article
      className={clsx("inspector", "inspector--wholesale", className)}
      // Named "WS-000042 Northside Body Shop": the number first, as the desk says it.
      aria-labelledby={`${numberId} ${titleId}`}
      data-order-id={order.id}
    >
      <header className="inspector__header">
        <div className="inspector__identity">
          <p id={numberId} className="inspector__case-number">
            {order.number}
          </p>
          <h2 id={titleId} className="inspector__title">
            {customer.name}
          </h2>
          <p className="inspector__customer">{order.customerReference ? `Ref ${order.customerReference}` : "No customer reference"}</p>
        </div>
        <div className="inspector__status">
          <StatusBadge status={order.status} needsAttention={order.needsAttention} />
          <p className="inspector__progress">
            {order.quantityVerified} of {order.quantityOrdered} verified
          </p>
          {order.status === "arrived" && order.activeUntil ? (
            <p className="inspector__window">On the Arrived list until {formatInstant(order.activeUntil)}</p>
          ) : null}
        </div>
      </header>

      <p role="status" aria-live="polite" className={clsx("inspector__announcement", !announcement && "inspector__announcement--empty")}>
        {announcement}
      </p>

      {(showVerify || showReverse || showEdit || showEmail || showComment || showCancel) && (
        <div className="inspector__actions" role="group" aria-label="Parts actions">
          {showVerify ? (
            <Button variant="primary" icon={<PackageCheck />} onClick={() => setVerifyOpen(true)}>
              Verify arrival
            </Button>
          ) : null}
          {showReverse ? (
            <Button variant="secondary" icon={<Undo2 />} onClick={() => setReverseOpen(true)}>
              Reverse verification
            </Button>
          ) : null}
          {showEdit ? (
            <Button variant="secondary" icon={<Pencil />} onClick={() => setEditOpen(true)}>
              Edit order
            </Button>
          ) : null}
          {showPrint ? (
            // A plain anchor into a new tab: the ticket page prints from there
            // and the workspace keeps its selection, view and filters.
            <a
              href={wholesaleTicketPath(order.id)}
              target="_blank"
              rel="noopener"
              {...buttonAttributes({ variant: "secondary" })}
            >
              <span className="button__icon" aria-hidden="true">
                <Printer />
              </span>
              <span className="button__label">Print order ticket</span>
            </a>
          ) : null}
          {showEmail ? (
            <span className="inspector__action-with-hint">
              <Button
                variant="secondary"
                icon={<Mail />}
                disabled={!emailEnabled}
                aria-describedby={emailHint ? emailHintId : undefined}
                onClick={() => setEmailOpen(true)}
              >
                Email customer
              </Button>
              {emailHint ? (
                <span id={emailHintId} className="inspector__action-hint">
                  {emailHint}
                </span>
              ) : null}
            </span>
          ) : null}
          {showComment ? (
            <Button variant="secondary" icon={<MessageSquarePlus />} onClick={() => setComposerOpen(true)}>
              Add comment
            </Button>
          ) : null}
          {showCancel ? (
            <Button variant="quiet" icon={<Ban />} className="inspector__cancel" onClick={() => setCancelOpen(true)}>
              Cancel order
            </Button>
          ) : null}
        </div>
      )}

      {emails.map((email) => (
        <EmailFailureBanner key={email.id} emailEventId={email.id} canRetry={capabilities.canRetryEmail} knownState={email.state} />
      ))}

      <div className="case-detail">
        <dl className="inspector__facts">
          <div className="inspector__fact">
            <dt>Contact</dt>
            <dd>{customer.contactName ?? "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>Phone</dt>
            <dd>{customer.phone ?? "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>Email</dt>
            <dd>{customer.email ?? "No email on file"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>Reference</dt>
            <dd>{order.customerReference ?? "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>R&amp;R number</dt>
            <dd>{order.rrNumber ?? "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>ETA for all parts</dt>
            <dd>{order.masterEta ? formatCalendarDate(order.masterEta) : "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>Arrived</dt>
            <dd>{order.arrivedAt ? formatInstant(order.arrivedAt) : "—"}</dd>
          </div>
          <div className="inspector__fact">
            <dt>Created</dt>
            <dd>
              {formatInstant(order.createdAt)} by {order.createdByName}
            </dd>
          </div>
          <div className="inspector__fact">
            <dt>Updated</dt>
            <dd>{formatInstant(order.updatedAt)}</dd>
          </div>
          {cancelled ? (
            <div className="inspector__fact inspector__fact--wide">
              <dt>Cancelled</dt>
              <dd>
                {order.cancelledAt ? formatInstant(order.cancelledAt) : "—"}
                {order.cancellationReason ? ` — ${order.cancellationReason}` : ""}
              </dd>
            </div>
          ) : null}
        </dl>

        {order.orderNote ? (
          <section className="inspector__section" aria-labelledby={noteTitleId}>
            <h3 id={noteTitleId} className="inspector__section-title">
              Order note
            </h3>
            <p className="inspector__note">{order.orderNote}</p>
          </section>
        ) : null}

        <section className="inspector__section" aria-labelledby={linesTitleId}>
          <h3 id={linesTitleId} className="inspector__section-title">
            Parts ordered
          </h3>
          <PartLinesTable partLines={partLines} />
        </section>

        <section className="inspector__section" aria-labelledby={emailsTitleId}>
          <h3 id={emailsTitleId} className="inspector__section-title">
            Customer emails <span className="inspector__count">({emails.length})</span>
          </h3>
          {emails.length === 0 ? (
            <p className="inspector__empty">No customer emails sent yet.</p>
          ) : (
            <ul className="inspector__comments" aria-label="Customer emails">
              {emails.map((email) => (
                <li key={email.id} className="inspector__comment" data-state={email.state}>
                  <p className="inspector__comment-meta">
                    <span className="inspector__comment-author">{email.requestedByName ?? "System"}</span>{" "}
                    <time dateTime={email.createdAt}>{formatInstant(email.createdAt)}</time>
                  </p>
                  <p className="inspector__comment-body">
                    To {email.to} — {EMAIL_STATE_LABELS[email.state]}
                    {email.state === "sent" && email.sentAt ? ` ${formatInstant(email.sentAt)}` : ""}
                    {email.state === "failed" && email.lastError ? `: ${email.lastError}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="inspector__section" aria-labelledby={commentsTitleId}>
          <h3 id={commentsTitleId} className="inspector__section-title">
            Comments <span className="inspector__count">({comments.length})</span>
          </h3>
          {comments.length === 0 ? (
            <p className="inspector__empty">No comments yet.</p>
          ) : (
            <ul className="inspector__comments">
              {comments.map((comment) => (
                <li key={comment.id} className="inspector__comment">
                  <p className="inspector__comment-meta">
                    <span className="inspector__comment-author">{comment.authorName}</span>{" "}
                    <time dateTime={comment.createdAt}>{formatInstant(comment.createdAt)}</time>
                  </p>
                  <p className="inspector__comment-body">{comment.body}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="inspector__section" aria-labelledby={activityTitleId}>
          <h3 id={activityTitleId} className="inspector__section-title">
            Activity
          </h3>
          <ActivityHistory
            ownerId={order.id}
            firstPage={activity}
            context={wholesaleActivityContext(detail)}
            olderKey={wholesaleQueryKeys.olderActivity(order.id, activity.nextCursor)}
            fetchOlderPage={(cursor) => fetchWholesaleActivityPage(order.id, cursor)}
          />
        </section>
      </div>

      {/* The dialogs are mounted on their own open state and work from a
          snapshot of the order, as on the service side; the server's version
          check answers any commit made after the order moved on. */}
      <VerifyArrivalDialog<WholesaleMutationResultDto>
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        caseId={order.id}
        caseNumber={order.number}
        side="wholesale"
        endpoints={wholesaleReceiptEndpoints(order.id)}
        version={order.version}
        status={order.status}
        partLines={partLines}
        technicianName={null}
        onVerified={onVerified}
      />
      <ReverseVerificationDialog<WholesaleMutationResultDto>
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        caseId={order.id}
        caseNumber={order.number}
        side="wholesale"
        endpoints={wholesaleReceiptEndpoints(order.id)}
        version={order.version}
        status={order.status}
        partLines={partLines}
        onReversed={onReversed}
      />
      <WholesaleOrderEditSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        detail={detail}
        onSaved={onSaved}
        onExceptionChanged={onExceptionChanged}
      />
      <CancelCaseDialog<WholesaleMutationResultDto>
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        caseId={order.id}
        caseNumber={order.number}
        customerName={customer.name}
        version={order.version}
        endpoint={(input) => cancelWholesaleOrder(order.id, input)}
        onCancelled={onCancelled}
      />
      <EmailCustomerDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        orderId={order.id}
        number={order.number}
        customerName={customer.name}
        onSent={onEmailSent}
      />
      <CommentComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        caseId={order.id}
        caseNumber={order.number}
        endpoints={wholesaleCommentEndpoints(order.id)}
        onAdded={onCommentAdded}
      />
    </article>
  );
}
